import { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import ProductGrid from '../components/shop/ProductGrid';
import ProductFilters from '../components/shop/ProductFilters';
import CategoryHeader from '../components/shop/CategoryHeader';
import { useProducts } from '../hooks/useProducts';
import { useCategories } from '../hooks/useCategories';
import SEO from '../components/SEO';

// Master lists — deben coincidir con los valores de tags en Medusa
const FILTER_CONFIG = {
  skinTypes: ['Normal', 'Seca', 'Grasa', 'Mixta', 'Sensible'],
  concerns: ['Acné', 'Manchas', 'Arrugas', 'Poros dilatados', 'Líneas de expresión', 'Envejecimiento', 'Hidratación', 'Luminosidad'],
  ingredients: ['Vitamina C', 'Retinol', 'Ácido Hialurónico', 'Niacinamida', 'Ácido Salicílico', 'Péptidos', 'Ceramidas', 'Vitamina E', 'Ácido Glicólico', 'Ácido Láctico'],
};

const SORT_OPTIONS = [
  { value: 'featured', label: 'Destacados' },
  { value: 'price-low', label: 'Precio – Menor a mayor' },
  { value: 'price-high', label: 'Precio – De mayor a menor' },
  { value: 'newest', label: 'Más Nuevos' },
  { value: 'name', label: 'Nombre A-Z' },
];

function SortDropdown({ value, onChange }) {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef(null);
  const currentLabel = SORT_OPTIONS.find(o => o.value === value)?.label || 'Destacados';

  const handleClickOutside = useCallback((e) => {
    if (ref.current && !ref.current.contains(e.target)) setIsOpen(false);
  }, []);

  useEffect(() => {
    if (isOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, handleClickOutside]);

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 text-sm tracking-wide font-light hover:opacity-60 transition-opacity"
      >
        <span>{currentLabel}</span>
        <svg
          width="10" height="6" viewBox="0 0 10 6" fill="none"
          className={`transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
        >
          <path d="M1 1L5 5L9 1" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>
      {isOpen && (
        <div className="absolute top-full right-0 mt-3 bg-[var(--phys-snow)] border border-[var(--phys-frost)] shadow-sm min-w-[240px] z-50 py-2">
          {SORT_OPTIONS.map(opt => (
            <label
              key={opt.value}
              className="flex items-center gap-3 px-5 py-3 cursor-pointer hover:bg-[var(--phys-mist)] transition-colors"
              onClick={() => { onChange(opt.value); setIsOpen(false); }}
            >
              <div className={`
                w-[18px] h-[18px] rounded-full border-[1.5px] flex items-center justify-center flex-shrink-0
                ${value === opt.value ? 'border-[var(--phys-ink)]' : 'border-[#ccc]'}
              `}>
                {value === opt.value && (
                  <div className="w-[8px] h-[8px] rounded-full bg-[var(--phys-ink)]" />
                )}
              </div>
              <span className="text-[13px] font-light">{opt.label}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

export default function ShopPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [showFilters, setShowFilters]   = useState(false);
  const { categories, getCategoryByHandle } = useCategories();

  // Estado derivado de la URL
  const searchParamsString = searchParams.toString();
  const activeCategory = searchParams.get('category') || 'todo';
  const sortBy         = searchParams.get('sort')     || 'featured';
  // ?badge=essential|launch|basic → solo productos con ese badge (links de /curated-selection)
  const activeBadge    = searchParams.get('badge');

  const activeFilters = useMemo(() => {
    const params = new URLSearchParams(searchParamsString);
    return {
      skinTypes:   params.get('skin')?.split(',').filter(Boolean)       || [],
      concerns:    params.get('concern')?.split(',').filter(Boolean)    || [],
      ingredients: params.get('ingredient')?.split(',').filter(Boolean) || [],
    };
  }, [searchParamsString]);

  // Helpers que actualizan la URL
  const setActiveCategory = (cat) =>
    setSearchParams(p => { cat === 'todo' ? p.delete('category') : p.set('category', cat); return p; });

  const setSortBy = (s) =>
    setSearchParams(p => { s === 'featured' ? p.delete('sort') : p.set('sort', s); return p; });

  const handleApplyFilters = ({ skinTypes, concerns, ingredients }) => {
    setSearchParams(p => {
      skinTypes.length   ? p.set('skin', skinTypes.join(','))         : p.delete('skin');
      concerns.length    ? p.set('concern', concerns.join(','))       : p.delete('concern');
      ingredients.length ? p.set('ingredient', ingredients.join(',')) : p.delete('ingredient');
      return p;
    });
  };

  // Badge count
  const activeFiltersCount =
    activeFilters.skinTypes.length +
    activeFilters.concerns.length +
    activeFilters.ingredients.length;

  // filterParams estable para el hook (evita loops de re-render)
  const filterParams = useMemo(() => ({
    ...activeFilters,
    categoryHandle: activeCategory,
  }), [activeFilters, activeCategory]);

  const { products, allProducts, isLoading, error } = useProducts(filterParams);

  // availableTags desde la carga inicial completa (no cambia al filtrar)
  const availableTags = useMemo(() => {
    const allTags = new Set(allProducts.flatMap(p => p.tags || []));
    return {
      skinTypes:   FILTER_CONFIG.skinTypes.filter(t => allTags.has(t)),
      concerns:    FILTER_CONFIG.concerns.filter(t => allTags.has(t)),
      ingredients: FILTER_CONFIG.ingredients.filter(t => allTags.has(t)),
    };
  }, [allProducts]);

  return (
    <div className="min-h-screen bg-[var(--phys-snow)]" data-header-theme="light">
      <SEO
        title="Tienda — Sérums, cremas, kits de cuidado facial"
        description="Explora el catálogo Physiology Skincare Co. Sérums con vitamina C, cremas hidratantes, kits de tratamiento y rituales para todo tipo de piel."
        path="/shop"
      />
      {/* Navigation Bar */}
      <div className="sticky top-0 z-40 bg-[var(--phys-snow)] border-b border-[var(--phys-frost)]">
        <div className="max-w-[2550px] mx-auto px-4 md:px-8">

          {/* Desktop: Single Row */}
          <div className="hidden md:flex items-center justify-between py-4">
            {/* Categories - Left */}
            <div className="flex items-center gap-6 overflow-x-auto scrollbar-hide pb-1">
              {categories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => setActiveCategory(category.id)}
                  className="group relative whitespace-nowrap text-sm font-normal transition-all duration-200"
                >
                  <span className={`relative inline-block ${
                    activeCategory === category.id
                      ? 'text-[var(--phys-ink)]'
                      : 'text-[var(--phys-slate)] group-hover:text-[var(--phys-ink)]'
                  }`}>
                    {category.name}
                    {activeCategory === category.id && (
                      <span className="absolute -bottom-1 left-0 w-full h-[1.5px] bg-[#1a1a1a]" />
                    )}
                  </span>
                </button>
              ))}
            </div>

            {/* Filters + Sort - Right */}
            <div className="flex items-center gap-6 flex-shrink-0 ml-8">
              {/* Filters Button */}
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="flex items-center gap-2 text-sm tracking-wide font-light hover:opacity-60 transition-opacity"
              >
                <span>Filtros</span>
                {activeFiltersCount > 0 ? (
                  <span className="w-5 h-5 rounded-full bg-[#1a1a1a] text-white text-[11px] flex items-center justify-center">
                    {activeFiltersCount}
                  </span>
                ) : (
                  <span className="text-lg leading-none">+</span>
                )}
              </button>

              {/* Separator */}
              <div className="w-px h-4 bg-[var(--phys-frost)]" />

              {/* Sort Dropdown */}
              <SortDropdown value={sortBy} onChange={setSortBy} />
            </div>
          </div>

          {/* Mobile: Two Rows */}
          <div className="md:hidden">
            {/* Row 1: Categories */}
            <div className="flex items-center gap-5 overflow-x-auto scrollbar-hide py-3 border-b border-[var(--phys-frost)]">
              {categories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => setActiveCategory(category.id)}
                  className="group relative whitespace-nowrap text-sm font-normal transition-all duration-200"
                >
                  <span className={`relative inline-block ${
                    activeCategory === category.id
                      ? 'text-[var(--phys-ink)]'
                      : 'text-[var(--phys-slate)]'
                  }`}>
                    {category.name}
                    {activeCategory === category.id && (
                      <span className="absolute -bottom-1 left-0 w-full h-[1.5px] bg-[#1a1a1a]" />
                    )}
                  </span>
                </button>
              ))}
            </div>

            {/* Row 2: Filters + Sort */}
            <div className="flex items-center justify-between py-3">
              {/* Filters Button */}
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="flex items-center gap-2 text-sm tracking-wide font-light"
              >
                <span>Filtros</span>
                {activeFiltersCount > 0 ? (
                  <span className="w-5 h-5 rounded-full bg-[#1a1a1a] text-white text-[11px] flex items-center justify-center">
                    {activeFiltersCount}
                  </span>
                ) : (
                  <span className="text-lg leading-none">+</span>
                )}
              </button>

              {/* Sort Dropdown */}
              <SortDropdown value={sortBy} onChange={setSortBy} />
            </div>
          </div>
        </div>
      </div>

      {/* Filters Panel */}
      <ProductFilters
        isOpen={showFilters}
        onClose={() => setShowFilters(false)}
        onApplyFilters={handleApplyFilters}
        availableTags={availableTags}
        activeFilters={activeFilters}
      />

      {/* Category Header */}
      <CategoryHeader handle={activeCategory} getCategoryByHandle={getCategoryByHandle} />

      {/* Product Grid */}
      <div className="w-full">
        <ProductGrid
          products={activeBadge ? products.filter((p) => p.badge === activeBadge) : products}
          isLoading={isLoading}
          error={error}
          sortBy={sortBy}
        />
      </div>
    </div>
  );
}
